/**
 * Progress dialog component for clone database operations.
 * Shows real-time progress, allows cancellation, and displays success/error states.
 */

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Copy, ChevronDown, ChevronUp, Clipboard, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { toast } from '../../../components/ui/toaster';
import type { CloneDatabaseProgressEvent, CloneDatabaseResult } from '../types/cloneDatabase';
import { ExternalToolHowToFixDialog } from '../../shared/external-tools/components/ExternalToolHowToFixDialog';
import { inferExternalToolHelpFromText } from '../../shared/external-tools/externalToolHelp';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { writeClipboardText } from '@/lib/clipboard';
type CloneStatus = 'in-progress' | 'succeeded' | 'failed' | 'cancelled';

type CloneDatabaseProgressDialogProps = {
  open: boolean;
  onClose: () => void;
  operationId: string;
  sourceDatabaseName: string;
  targetDatabaseName: string;
  targetConnectionId?: string;
  onRefreshDatabases?: (connectionId: string) => void;
  onOpenInExplorer?: () => void;
};

function formatElapsedTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
}

export function CloneDatabaseProgressDialog({
  open,
  onClose,
  operationId,
  sourceDatabaseName,
  targetDatabaseName,
  targetConnectionId,
  onRefreshDatabases,
  onOpenInExplorer,
}: CloneDatabaseProgressDialogProps) {
  const [status, setStatus] = useState<CloneStatus>('in-progress');
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('Preparing...');
  const [currentItem, setCurrentItem] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [result, setResult] = useState<CloneDatabaseResult | undefined>();
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showHowToFix, setShowHowToFix] = useState(false);
  const startTimeRef = useRef<number>(Date.now());
  const didRefreshDatabasesRef = useRef(false);
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  useEffect(() => {
    didRefreshDatabasesRef.current = false;
  }, [operationId]);

  const externalToolHelp = useMemo(() => {
    if (status !== 'failed') return null;
    if (!error) return null;
    return inferExternalToolHelpFromText(error);
  }, [status, error]);

  const applyProgressEvent = useCallback((event: CloneDatabaseProgressEvent) => {
    setMessage(event.message);
    setCurrentItem(event.currentItem);

    if (event.progress !== undefined) {
      setProgress(event.progress);
    }

    switch (event.phase) {
      case 'complete':
        if (!didRefreshDatabasesRef.current && targetConnectionId && onRefreshDatabases) {
          didRefreshDatabasesRef.current = true;
          onRefreshDatabases(targetConnectionId);
        }
        setStatus('succeeded');
        setResult({
          success: true,
          targetDatabaseName,
          targetConnectionId,
          elapsedMs: Date.now() - startTimeRef.current,
        });
        break;
      case 'failed':
        setStatus('failed');
        setError(event.error || event.message);
        setShowHowToFix(false);
        break;
      case 'cancelled':
        setStatus('cancelled');
        break;
      default:
        setStatus('in-progress');
    }
  }, [onRefreshDatabases, targetConnectionId, targetDatabaseName]);

  // Update elapsed time every second
  useEffect(() => {
    if (status !== 'in-progress') return;

    const interval = setInterval(() => {
      setElapsedTime(Date.now() - startTimeRef.current);
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  // Subscribe to progress events
  useEffect(() => {
    if (!operationId || !window.electronAPI?.cloneDatabase) return;

    let isActive = true;

    const unsubscribe = window.electronAPI.cloneDatabase.onProgress(
      (event: CloneDatabaseProgressEvent) => {
        if (!isActive) return;
        if (event.operationId !== operationId) return;
        applyProgressEvent(event);
      }
    );

    // Avoid a race where the operation emits progress/failure before the dialog subscribes.
    void window.electronAPI.cloneDatabase.getLatestProgress({ operationId }).then((latest) => {
      if (!isActive) return;
      if (!latest) return;
      if (latest.operationId !== operationId) return;
      applyProgressEvent(latest);
    });

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [operationId, applyProgressEvent]);

  const handleCancel = useCallback(async () => {
    if (!window.electronAPI?.cloneDatabase) return;

    setIsCancelling(true);
    setMessage('Cancelling...');
    try {
      const response = await window.electronAPI.cloneDatabase.cancel({ operationId });
      if (!response.success) {
        toast.error({
          title: 'Cancel Failed',
          description: response.error || 'Could not cancel operation',
        });
      }
    } catch (err) {
      toast.error({
        title: 'Cancel Failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsCancelling(false);
      setShowCancelConfirm(false);
    }
  }, [operationId]);

  const handleCopyError = useCallback(() => {
    if (error) {
      void writeClipboardText(error);
      toast.success({ title: 'Error copied to clipboard' });
    }
  }, [error]);

  const handleClose = useCallback(() => {
    if (status === 'in-progress') {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  }, [status, onClose]);

  const getStatusIcon = () => {
    switch (status) {
      case 'succeeded':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'cancelled':
        return <AlertCircle className="h-5 w-5 text-muted-foreground" />;
      default:
        return <RandomDotmSpinner size={20} className="text-primary" />;
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'succeeded':
        return 'Clone Complete';
      case 'failed':
        return 'Clone Failed';
      case 'cancelled':
        return 'Clone Cancelled';
      default:
        return 'Cloning Database...';
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent
          data-testid="clone-database-progress-dialog"
          className="sm:max-w-[500px]"
        >
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <Copy className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <DialogTitle>{getStatusLabel()}</DialogTitle>
                <DialogDescription className="mt-1">
                  {sourceDatabaseName} → {targetDatabaseName}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Status header */}
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <span className="font-medium">{message}</span>
              {status === 'in-progress' && (
                <span className="text-sm text-muted-foreground ml-auto tabular-nums">
                  {Math.round(progress)}%
                </span>
              )}
            </div>

            {/* Progress bar */}
            {status === 'in-progress' && (
              <Progress
                value={progress}
                className="h-2"
                data-testid="clone-progress-bar"
              />
            )}

            {/* Current item */}
            {currentItem && status === 'in-progress' && (
              <p
                className="text-sm text-muted-foreground"
                data-testid="clone-progress-current-item"
              >
                {currentItem}
              </p>
            )}

            {/* Elapsed time */}
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Elapsed time:</span>
              <span data-testid="clone-elapsed-time" className="tabular-nums">
                {formatElapsedTime(
                  status === 'in-progress' ? elapsedTime : (result?.elapsedMs || elapsedTime)
                )}
              </span>
            </div>

            {/* Success summary */}
            {status === 'succeeded' && result && (
              <div
                className="p-3 rounded-md bg-green-500/10 border border-green-500/30"
                data-testid="clone-success-banner"
              >
                <p className="text-sm text-green-700 dark:text-green-400">
                  Database cloned successfully.
                  {result.objectsCloned !== undefined && (
                    <> {result.objectsCloned} objects</>
                  )}
                  {result.rowsCopied !== undefined && result.rowsCopied > 0 && (
                    <>, {result.rowsCopied.toLocaleString()} rows</>
                  )}
                  {' copied.'}
                </p>
              </div>
            )}

            {/* Error display */}
            {status === 'failed' && error && (
              <div className="space-y-2">
                <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30">
                  <p
                    className="text-sm text-destructive"
                    data-testid="clone-error-message"
                  >
                    {error.length > 200 && !showErrorDetails
                      ? `${error.substring(0, 200)}...`
                      : error}
                  </p>
                </div>

                {error.length > 200 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowErrorDetails(!showErrorDetails)}
                    className="text-xs"
                  >
                    {showErrorDetails ? (
                      <>
                        <ChevronUp className="h-3 w-3 mr-1" />
                        Show less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3 mr-1" />
                        View details
                      </>
                    )}
                  </Button>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {externalToolHelp && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowHowToFix(true)}
                      className="text-xs"
                      data-testid="clone-error-how-to-fix"
                    >
                      How to fix
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyError}
                    className="text-xs"
                  >
                    <Clipboard className="h-3 w-3 mr-1" />
                    Copy error
                  </Button>
                </div>
              </div>
            )}

            {/* Cancelled message */}
            {status === 'cancelled' && (
              <div className="p-3 rounded-md bg-muted border border-border">
                <p className="text-sm text-muted-foreground">
                  The clone operation was cancelled. Any partial data may have been cleaned up.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex items-center gap-2">
            {status === 'in-progress' ? (
              <Button
                variant="outline"
                onClick={() => setShowCancelConfirm(true)}
                disabled={isCancelling}
                data-testid="clone-cancel-button"
              >
                {isCancelling ? 'Cancelling...' : 'Cancel'}
              </Button>
            ) : (
              <>
                {status === 'succeeded' && onOpenInExplorer && (
                  <Button
                    variant="outline"
                    onClick={onOpenInExplorer}
                    data-testid="clone-open-explorer-button"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in Explorer
                  </Button>
                )}
                <Button
                  onClick={onClose}
                  data-testid="clone-close-button"
                >
                  {status === 'succeeded' ? 'Done' : 'Close'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Clone Operation?</AlertDialogTitle>
            <AlertDialogDescription>
              The clone operation is still in progress. Cancelling may leave partial data
              which will be cleaned up if possible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clone-cancel-confirm-no">
              Continue Cloning
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-destructive-fill text-destructive-foreground hover:bg-destructive-fill/90"
              data-testid="clone-cancel-confirm-yes"
            >
              Cancel Clone
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {externalToolHelp && (
        <ExternalToolHowToFixDialog
          open={showHowToFix}
          onOpenChange={setShowHowToFix}
          topic={externalToolHelp.topic}
          missingTools={externalToolHelp.missingTools}
        />
      )}
    </>
  );
}
